// Credits: borrowed code from fcomb/redux-logger

import { Mutation, Store, MutationPayload } from "vuex";

/**
 * Get the first item that pass the test
 * by second argument function
 *
 * @param {Array} list
 * @param {Function} f
 * @return {*}
 */
export function find(
  list: Array<any>,
  f: (value: any, index: number, array: any[]) => boolean
): any {
  return list.filter(f)[0];
}

interface CacheEntry {
  original: any;
  copy: any;
}

/**
 * Deep copy the given object considering circular structure.
 * This function caches all nested objects and its copies.
 * If it detects circular structure, use cached copy to avoid infinite loop.
 *
 * @param {*} obj
 * @param {Array<Object>} cache
 * @return {*}
 */
export function deepCopy(obj: any, cache: Array<CacheEntry> = []) {
  // just return if obj is immutable value
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // if obj is hit, it is in circular structure
  const hit = find(cache, (c) => c.original === obj);
  if (hit) {
    return hit.copy;
  }

  const copy: any = Array.isArray(obj) ? [] : {};
  // put the copy into cache at first
  // because we want to refer it in recursive deepCopy
  cache.push({
    original: obj,
    copy,
  });

  Object.keys(obj).forEach((key) => {
    copy[key] = deepCopy(obj[key], cache);
  });

  return copy;
}

function repeat(str: string, times: number) {
  return new Array(times + 1).join(str);
}

function pad(num: number, maxLength: number) {
  return repeat("0", maxLength - num.toString().length) + num;
}

export default function createLogger<S extends Store<any>>({
  collapsed = true,
  filter = (mutation: MutationPayload, stateBefore: any, stateAfter: any) =>
    true,
  transformer = (state: S) => state,
  mutationTransformer = (mut: MutationPayload) => mut,
  logger = console,
} = {}) {
  return (store: S) => {
    let prevState = deepCopy(store.state);

    store.subscribe((mutation: MutationPayload, state: any) => {
      if (typeof logger === "undefined") {
        return;
      }
      const nextState = deepCopy(state);

      if (filter(mutation, prevState, nextState)) {
        const time = new Date();
        const formattedTime = ` @ ${pad(time.getHours(), 2)}:${pad(
          time.getMinutes(),
          2
        )}:${pad(time.getSeconds(), 2)}.${pad(time.getMilliseconds(), 3)}`;
        const formattedMutation = mutationTransformer(mutation);
        const message = `mutation ${mutation.type}${formattedTime}`;
        const startMessage = collapsed ? logger.groupCollapsed : logger.group;

        // render
        try {
          startMessage.call(logger, message);
        } catch (e) {
          console.log(message);
        }

        logger.log(
          "%c prev state",
          "color: #9E9E9E; font-weight: bold",
          transformer(prevState)
        );
        logger.log(
          "%c mutation",
          "color: #03A9F4; font-weight: bold",
          formattedMutation
        );
        logger.log(
          "%c next state",
          "color: #4CAF50; font-weight: bold",
          transformer(nextState)
        );

        try {
          logger.groupEnd();
        } catch (e) {
          logger.log("—— log end ——");
        }
      }

      prevState = nextState;
    });
  };
}
